import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import API from '@/lib/api'
import { Role, RoleType } from '@/types/User'

interface InviteUserFormProps {
  onSuccess: () => void
}

interface InviteUserFormValues {
  email: string
  fullName: string
  role: RoleType
}

export const InviteUserForm = ({ onSuccess }: InviteUserFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.users.add',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<InviteUserFormValues>({
    defaultValues: {
      role: 'accountant',
    },
  })

  const onSubmit = (values: InviteUserFormValues) => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)

    API.post('/api/v2/user-invitations', {
      email: values.email,
      fullName: values.fullName,
      roles: [values.role],
    })
      .then(() => {
        toast.success(t('form.submit.success'))
        onSuccess()
      })
      .catch((err) => {
        console.error(err)
        toast.error(t('form.submit.error'))
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const roleOptions = [
    {
      label: 'Účetní',
      value: Role.accountant,
      description: 'Účetní má přístup k dokladům a bance pouze pro čtení.',
    },
    {
      label: 'Zaměstnanec',
      value: Role.employee,
      description:
        'Zaměstnanec má přístup k dokladům, může je upravovat a vytvářet nové. Nemůže měnit žádné nastavení.',
    },
    {
      label: 'Správce',
      value: Role.manager,
      description:
        'Správce má přístup k všem nastavením a dokladům účtu a může přidávat zaměstnance a účetní.',
    },
  ]

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.fullName.label')}</FormLabel>
              <FormControl>
                <Input {...field} autoComplete="off" required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.email.label')}</FormLabel>
              <FormControl>
                <Input type="email" autoComplete="off" required {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel>{t('form.role.label')}</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="gap-0 divide-y overflow-hidden rounded-lg border"
                >
                  {roleOptions.map((role) => (
                    <label
                      key={role.value}
                      htmlFor={`invite-role-${role.value}`}
                      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-muted has-[[data-state=checked]]:bg-muted"
                    >
                      <RadioGroupItem
                        value={role.value}
                        id={`invite-role-${role.value}`}
                        className="mt-0.5"
                      />
                      <span className="space-y-0.5">
                        <span className="block text-sm font-medium">{role.label}</span>
                        <span className="block text-sm text-muted-foreground">{role.description}</span>
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button className="w-full" disabled={isSubmitting} type="submit">
          {isSubmitting ? tg('sending') : t('form.submit.label')}
        </Button>
      </form>
    </FormProvider>
  )
}
